import toArray from 'lodash/toArray';
import defaults from 'lodash/defaults';
import { resultsConfig } from '@Constants/Results';
import { bioSortingData } from '@Constants/BioSortingData';
import UserDatabases from '@Services/UserDatabases';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import * as qbHelpersService from '@Services/QBHelpers';
import * as Results from '@Services/Results';
import Breadcrumb from '@Services/Breadcrumb';
import SubstanceData from '@Services/SubstanceData';
import * as BioData from '@Services/BioData';
import Catalyst from '@Services/Catalyst';

const SubstanceHelper = {
  getCurrentDatabase(databaseId) {
    const database = UserDatabases.getDatabaseById(databaseId);
    database.isCIDatabase = UserDatabases.isCIDatabase(databaseId);
    database.isReaxysContent = UserDatabases.isDefaultDatabase(databaseId);
    database.isPubChem = databaseId === '1';
    database.isMarketSelect = databaseId === '18';
    database.supportsSupplierData = UserDatabases.supportsSupplierData(databaseId);
    database.isCommercialSupplierData = UserDatabases.isRCSDatabase(databaseId);

    return database;
  },

  checkAndRetrieveFactData(fact, substance, requestParams) {
    // for heatmap bioactivity popup resultName from fact itself should be used
    // eslint-disable-next-line no-param-reassign
    requestParams.resultName = requestParams.heatmapMode ? fact.resultName : requestParams.resultName;

    const {
      databaseId,
      index,
      details: { id: substanceId },
    } = substance;
    const { resultName, sort, order } = requestParams;
    const retrieveData = fact.type === 'bio' || fact.type === 'calc' || fact.type === 'category';

    if (retrieveData && !fact.bioData && !fact.drugData && !fact.children) {
      switch (fact.type) {
        case 'calc':
          return (
            requestParams.heatmapMode
              ? SubstanceData.retrieveDruglikenessDataByXrn({
                  databaseId: requestParams.nestedDatabaseId,
                  xrn: substanceId,
                })
              : SubstanceData.retrieveDruglikenessData({
                  type: fact.type,
                  item: index,
                  databaseId,
                  resultName,
                  sort,
                  order,
                })
          )
            .then((response) => {
              // eslint-disable-next-line no-param-reassign
              fact.drugData = response;
            })
            .catch(Logger.reject);

        case 'bio':
          return this.retrieveBioCategories(fact, databaseId, substanceId, requestParams);

        case 'category':
          return this.retrieveBioCategories(fact, databaseId, substanceId, requestParams);

        default:
          Logger.warn(`Fact type '${fact.type}' is not supported.`);
      }
    }

    return Promise.resolve();
  },

  // eslint-disable-next-line consistent-return
  retrieveBioCategories(fact, databaseId, xrn, requestParams) {
    const params = { type: fact.type, databaseId, xrn };

    if (!requestParams.heatmapMode) {
      if (fact.id === 'BIO_MED_HD') {
        // eslint-disable-next-line no-use-before-define
        return retrieveBioCategories(
          {
            ...params,
            resultName: fact.bioResultname,
          },
          fact
        );
      }
      // eslint-disable-next-line no-use-before-define
      return retrieveBioCategories(params, fact);
    }
    if (requestParams.heatmapMode) {
      return SubstanceData.retrieveBioCategoryData({
        type: 'bio',
        databaseId: requestParams.databaseId,
        index: fact.index,
        resultName: requestParams.resultName,
      })
        .then((data) => {
          // eslint-disable-next-line no-param-reassign
          fact.bioData = {
            qual: data.qual,
            quan: data.quan,
            databaseId: data.databaseId,
          };
        })
        .catch(Logger.reject);
    }

    // eslint-disable-next-line no-shadow
    function retrieveBioCategories(params, fact) {
      return SubstanceData.retrieveBioCategories(params)
        .then((data) => {
          // eslint-disable-next-line no-param-reassign
          fact.children = data.categories;
          // eslint-disable-next-line no-param-reassign
          fact.nestedResultName = data.resultName;
        })
        .catch(Logger.reject);
    }
  },

  retrieveBioCategoryData(resultName, databaseId, index, trackData) {
    return SubstanceData.retrieveBioCategoryData({ databaseId, index, resultName })
      .then((data) => {
        // eslint-disable-next-line no-shadow
        const { qual, quan, databaseId } = SubstanceData.handleTargetTitles(data);
        return {
          qual,
          quan,
          databaseId,
          // eslint-disable-next-line no-use-before-define
          type: getTrackedType(),
        };
      })
      .catch(Logger.reject);

    function getTrackedType() {
      const pageName = {
        'main.search.quick.results': 'rx2:query:preview',
        'main.results.context.substances': 'rx2:results:substances',
        'main.results.context.reactions': 'rx2:results:reactions',
      };
      const context = trackData.context ? `.${trackData.context}` : '';
      const page = pageName[trackData.pageName + context];

      return `${page}:${(trackData.factTitle ? `${trackData.factTitle}:` : '') + trackData.childTitle}`;
    }
  },

  retrieveBioContent(type, categoryName, databaseId, resultName, firstItem, lastItem) {
    const { sort, order } = bioSortingData[type];

    return SubstanceData.retrieveBioContent({ databaseId, resultName, firstItem, lastItem, sort, order })
      .then((data) => {
        const entities = toArray(data.entities);
        entities.forEach(SubstanceData.renameTitles);

        if (type === 'quan') {
          return BioData.changeQuanDataFormat(categoryName, { data: entities }).formatedData;
        }

        return entities;
      })
      .catch(Logger.reject);
  },

  retrieveFactData(index, fact, databaseId, requestParams, firstItem, lastItem) {
    const { resultName, sort, order, nestedResultName, heatmapMode } = requestParams;

    const payload = { item: index, fact: fact.format || fact.id };
    const additionalPayload = {
      databaseId,
      firstItem,
      lastItem,
      resultName,
      sort,
      order,
      hitData: fact.hitData,
      context: resultsConfig.CONTEXT.substances,
    };

    if (heatmapMode) {
      additionalPayload.resultName = nestedResultName || resultName;
      delete additionalPayload.sort;
      delete additionalPayload.order;
    }

    return SubstanceData.retrieveFactData(payload, additionalPayload);
  },

  openElementLink(type, substance, requestParams) {
    const searchParams = {
      ...requestParams,
      databaseId: substance.databaseId,
      index: substance.index,
      id: qbHelpersService.getElementId('substances', substance),
    };

    Catalyst.trackContextLinkClick(type);

    return Results.getRelatedResults(type, searchParams, 'substances')
      .then((data) => {
        const { searchId } = searchParams;
        defaults(data, { searchId });

        Breadcrumb.createBreadcrumb(data, data.resultSize)
          .then(() => {
            const { resultName, context, databaseId, hitSetKey } = data;
            ResultsDataManage.redirectToResults({
              resultName,
              context,
              databaseId,
              hitSetKey,
              searchId,
              resetBreadcrumbs: false, // don't reset the breadcrumbs
            });
          })
          .catch(Logger.error);
      })
      .catch(Logger.reject);
  },
};

export default SubstanceHelper;
